import { defineSettingsProvider } from "./provider";
import {
  FlatTeamSettingsCatalogDefinitions as LegacyFlatTeamSettingsCatalogDefinitions,
  FlatTeamSettingsCatalogGroups as LegacyFlatTeamSettingsCatalogGroups,
} from "./providers/flat-team-settings";
import {
  FlatTeamSettingsCatalogDefinitions,
  FlatTeamSettingsCatalogGroups,
} from "./providers/flat-team-settings-shipping-new";
import {
  IntegrationsHubCatalogDefinitions,
  IntegrationsHubCatalogGroups,
} from "./providers/integrations-hub";
import type {
  SettingDefinition,
  SettingGroupDefinition,
  SettingProductArea,
  SettingValueSource,
  TeamSettingsSettingValueSource,
} from "./types";

export type {
  ExternalSettingValueSource,
  SettingAccess,
  SettingAgentEditability,
  SettingAgentEditabilityMode,
  SettingAvailability,
  SettingAvailabilityCondition,
  SettingAvailabilityContext,
  SettingAvailabilityEvaluatorCondition,
  SettingAvailabilityEvaluatorDataRequirements,
  SettingAvailabilityEvaluatorId,
  SettingAvailabilityIntegrationKey,
  SettingAvailabilityMissingContextBehavior,
  SettingAvailabilitySettingEqualsCondition,
  SettingAvailabilityState,
  SettingAvailabilityUnavailableBehavior,
  SettingChangeGuidance,
  SettingDefinition,
  SettingDependency,
  SettingDependencyTarget,
  SettingDirectWriteGrant,
  SettingGroupChatSurface,
  SettingGroupDefinition,
  SettingGroupFullPageTarget,
  SettingMetadata,
  SettingOwner,
  SettingPermission,
  SettingProductArea,
  SettingProviderWriteGrant,
  SettingReadAccess,
  SettingReadValueVisibility,
  SettingRegistryMetadataInput,
  SettingRisk,
  SettingRiskInput,
  SettingRiskLevel,
  SettingSourceKind,
  SettingValueAdapterId,
  SettingValueSemantics,
  SettingValueSource,
  SettingVisibility,
  SettingWorkflowWriteGrant,
  SettingWriteGrant,
  SettingWriteGrantBase,
  TeamSettingsSettingValueSource,
} from "./types";

export {
  canDirectlyWriteSetting,
  canReadSetting,
  canViewSetting,
  getDirectSettingWriteGrants,
  getMostRestrictiveSettingVisibility,
  SettingVisibilityRank,
} from "./access";
export {
  evaluateSettingAvailability,
  getCombinedSettingAvailabilityEvaluatorDataRequirements,
  getSettingAvailabilityConditionEvaluatorIds,
  getSettingAvailabilityConditionSettingIds,
  getSettingAvailabilityEvaluatorDataRequirements,
  isSettingAvailabilitySearchVisible,
  SETTING_AVAILABILITY_EVALUATORS,
  SETTING_AVAILABILITY_UNAVAILABLE_BEHAVIORS,
  shouldShowSettingsForAvailability,
} from "./availability";
export {
  createSettingMetadataFromGroupDefaults,
  getDefaultSettingAgentEditability,
} from "./metadata";
export type {
  SettingMetadataAgentEditabilityResolver,
  SettingMetadataDefaultsContext,
} from "./metadata";
export type { SettingProvider } from "./provider";
export {
  SETTING_AVAILABILITY_EVALUATOR_IDS,
  SETTING_AVAILABILITY_INTEGRATION_KEYS,
  SETTING_AVAILABILITY_MISSING_CONTEXT_BEHAVIORS,
  SETTING_PRODUCT_AREAS,
} from "./types";

export const SettingCatalogDefinitions = [
  ...FlatTeamSettingsCatalogDefinitions,
  ...IntegrationsHubCatalogDefinitions,
] as const satisfies readonly SettingDefinition[];

export const SettingCatalogGroups = [
  ...FlatTeamSettingsCatalogGroups,
  ...IntegrationsHubCatalogGroups,
] as const satisfies readonly SettingGroupDefinition[];

export const SettingCatalogProvider = defineSettingsProvider({
  name: "settings catalog",
  groups: SettingCatalogGroups,
  definitions: SettingCatalogDefinitions,
});

const legacyCatalogGroups: readonly SettingGroupDefinition[] = [
  ...LegacyFlatTeamSettingsCatalogGroups,
  ...IntegrationsHubCatalogGroups,
];
const legacyCatalogDefinitions: readonly SettingDefinition[] = [
  ...LegacyFlatTeamSettingsCatalogDefinitions,
  ...IntegrationsHubCatalogDefinitions,
];

export function getSettingCatalogGroups(
  shippingNewSettings = false,
): readonly SettingGroupDefinition[] {
  return shippingNewSettings ? SettingCatalogGroups : legacyCatalogGroups;
}

export function getSettingCatalogDefinitions(
  shippingNewSettings = false,
): readonly SettingDefinition[] {
  return shippingNewSettings
    ? SettingCatalogDefinitions
    : legacyCatalogDefinitions;
}

export const SettingCatalogDefinitionById: Readonly<
  Record<string, SettingDefinition>
> = SettingCatalogProvider.definitionById;

export function getSettingCatalogDefinition(
  id: string,
  shippingNewSettings = false,
): SettingDefinition | undefined {
  return getSettingCatalogDefinitions(shippingNewSettings).find(
    (definition) => definition.id === id,
  );
}

export function getSettingCatalogGroup(
  groupId: string,
  shippingNewSettings = false,
): SettingGroupDefinition | undefined {
  return getSettingCatalogGroups(shippingNewSettings).find(
    (group) => group.id === groupId,
  );
}

export function getSettingCatalogDefinitionsForGroup(
  groupId: string,
  shippingNewSettings = false,
): readonly SettingDefinition[] {
  return getSettingCatalogDefinitions(shippingNewSettings).filter(
    (definition) => {
      const metadata = definition.metadata;
      return (
        definition.groupId === groupId ||
        metadata.relatedGroups?.includes(groupId)
      );
    },
  );
}

export function getSettingCatalogDefinitionsForTeamSettingsKey(
  key: string,
  shippingNewSettings = false,
): readonly SettingDefinition[] {
  return getSettingCatalogDefinitions(shippingNewSettings).filter(
    (definition) => {
      return (
        isTeamSettingsSettingValueSource(definition.valueSource) &&
        definition.valueSource.key === key
      );
    },
  );
}

export function getSettingCatalogGroupsForProductArea(
  productArea: SettingProductArea,
  shippingNewSettings = false,
): readonly SettingGroupDefinition[] {
  return getSettingCatalogGroups(shippingNewSettings).filter((group) => {
    return group.productArea === productArea;
  });
}

export function isTeamSettingsSettingValueSource(
  valueSource: SettingValueSource,
): valueSource is TeamSettingsSettingValueSource {
  return valueSource.adapter === "teamSettings";
}

export { defineSettingsProvider } from "./provider";

export { settingsWrapperManifest } from "./settings-wrapper-manifest.generated";
export type {
  SettingsWrapperManifest,
  SettingsWrapperManifestSection,
  SettingsWrapperManifestSetting,
} from "./settings-wrapper-manifest.types";
